import { Button, Divider, HStack, Link, List, ListItem, Text, VStack } from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { CAPTURE_SURFACE_ONBOARDING_IMPORT } from '@bower/shared';

import { IMPORT_MAX_FILES, useImportFiles } from '../../hooks/upload/useImportFiles';
import { HELP_LINKS } from '../../constants/helpLinks';
import { ROUTES } from '../../constants/routes';
import type { CreationTrigger } from '../../types/analytics';
import { ConnectorBrandIcon, type ConnectorBrandIconName } from '../ui/brand/ConnectorBrandIcon';
import { MaterialIcon } from '../ui/MaterialIcon';
import { DropZone } from '../upload/intake/DropZone';
import { ANY_FILE } from '../upload/intake/rules';
import { useFileIntake } from '../upload/intake/useFileIntake';

export interface ImportFilesPanelProps {
  readonly parentEntityType: 'project' | 'workspace';
  readonly parentEntityId: string;
  /** Fired at SUBMIT time, once the files are handed to the background tray,
   * NOT when the upload completes. See `ImportFilesModal`. */
  readonly onSubmitted?: () => void;
  /** Target artifact type for AI extraction; 'note' when omitted. */
  readonly targetArtifactType?: string;
  /** Suppress the "import from connected apps" hint (onboarding). */
  readonly hideConnectorHint?: boolean;
  /** Onboarding's lifecycle surface; see `ImportFilesModal` for why it is narrow. */
  readonly captureSurface?: typeof CAPTURE_SURFACE_ONBOARDING_IMPORT;
  /** Analytics `creation_trigger` of the import; 'import_modal' when omitted. */
  readonly trigger?: CreationTrigger;
  /** The panel is finished: the files went to the upload tray, or the user
   * left for Connectors settings. The host closes. */
  readonly onDone: () => void;
  /** When given, a Cancel button sits beside Upload. */
  readonly onCancel?: () => void;
}

const MAX_FILES = IMPORT_MAX_FILES;

/**
 * The brand marks beside the hint, in display order, from the bundled
 * `ConnectorBrandIcon` artwork that every other connector surface uses. Not the
 * toolkit catalog's `iconUrl`: there OneNote has no logo of its own (its
 * `microsoft_workspace` entry reuses OneDrive's), so the hint lost its logo.
 * Only OneNote imports today (`backend/.../connectors/import/adapters/`); Google
 * is shown at David's request (2026-10-01) as a connect target, not an import
 * promise, so the copy still names OneNote only.
 */
const HINT_BRANDS: ReadonlyArray<ConnectorBrandIconName> = ['microsoft-onenote', 'google'];

function getFileIcon(file: File): string {
  const t = file.type;
  if (t.startsWith('image/')) return 'image';
  if (t.startsWith('audio/')) return 'audio_file';
  if (t.startsWith('video/')) return 'video_file';
  if (t === 'application/pdf') return 'picture_as_pdf';
  if (t.includes('wordprocessingml') || t === 'application/msword') return 'description';
  if (t.includes('spreadsheet') || t.includes('excel') || t === 'text/csv') return 'table_chart';
  if (t.includes('presentation') || t.includes('powerpoint')) return 'slideshow';
  if (t.startsWith('text/') || t === 'application/json' || t === 'application/xml') return 'code';
  if (t.includes('zip') || t.includes('rar') || t.includes('7z')) return 'folder_zip';
  return 'insert_drive_file';
}

/**
 * Editorial-whisper discoverability hint: reads as a continuation of the line
 * above it, no card or fill. The marks are decorative bundled artwork; the
 * button's aria-label names it.
 */
function ConnectorImportHint({ onNavigate }: { readonly onNavigate: () => void }) {
  return (
    <>
      <Divider borderColor="border.subtle" />
      <HStack
        as="button"
        type="button"
        aria-label="Import from connected apps: open Connectors settings"
        spacing={2}
        py={2}
        px={1}
        w="full"
        align="center"
        color="text.muted"
        cursor="pointer"
        bg="transparent"
        border="none"
        textAlign="left"
        transition="color 80ms ease"
        _hover={{ color: 'text.primary', textDecoration: 'none' }}
        _focusVisible={{
          outline: 'var(--chakra-borderWidths-interactive) solid',
          outlineColor: 'border.focus',
          borderRadius: 'sm',
          outlineOffset: '2px',
        }}
        onClick={onNavigate}
      >
        <HStack spacing={1.5} flexShrink={0}>
          {HINT_BRANDS.map((name) => (
            <ConnectorBrandIcon key={name} name={name} boxSize="16px" opacity={0.65} />
          ))}
        </HStack>
        <Text fontSize="sm" lineHeight="short" flex={1}>
          Already using OneNote?{' '}
          <Text
            as="span"
            textDecoration="underline"
            textUnderlineOffset="2px"
            textDecorationColor="currentColor"
          >
            Import your notebooks directly.
          </Text>
        </Text>
        <MaterialIcon name="chevron_right" boxSize={4} aria-hidden flexShrink={0} />
      </HStack>
    </>
  );
}

/**
 * Pick files and hand them to the background upload tray (intent 'import';
 * the server decides per file what each becomes). Shared by `ImportFilesModal`
 * and the Create modal's Import body. Renders as a flex column so a host can
 * give it a height cap: the file list is the part that gives and scrolls.
 */
export function ImportFilesPanel({
  parentEntityType,
  parentEntityId,
  onSubmitted,
  targetArtifactType = 'note',
  hideConnectorHint = false,
  captureSurface,
  trigger = 'import_modal',
  onDone,
  onCancel,
}: ImportFilesPanelProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  // Two clicks inside one event batch both see the old, non-empty selection;
  // the ref makes the second one a no-op so the tray gets the files once.
  const submittingRef = useRef(false);
  const navigate = useNavigate();
  const importFiles = useImportFiles();

  // The cap is per import, so a second drop gets only the room the first left;
  // the intake keeps the first ones that fit and says how many it skipped.
  const room = MAX_FILES - selectedFiles.length;
  const intake = useFileIntake({
    rule: ANY_FILE,
    surface: 'import',
    maxFiles: Math.max(room, 1),
    readOnlyReason:
      room > 0 ? undefined : `${MAX_FILES} files is the most per import. Upload these first.`,
    overLimitHint: (skipped) =>
      `${MAX_FILES} files is the most per import. Upload these, then import the other ${skipped}.`,
    onFiles: (files) => {
      submittingRef.current = false;
      setSelectedFiles((prev) => [...prev, ...files]);
    },
  });

  const handleImport = () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    const started = importFiles(selectedFiles, {
      parent: { type: parentEntityType, id: parentEntityId },
      trigger,
      targetArtifactType,
      captureSurface,
    });
    if (!started) {
      submittingRef.current = false;
      return;
    }
    setSelectedFiles([]);
    onSubmitted?.();
    onDone();
  };

  const handleConnectorHintClick = () => {
    setSelectedFiles([]);
    onDone();
    navigate(`${ROUTES.settings}?tab=connectors`);
  };

  return (
    <>
      <VStack spacing={4} align="stretch" flex={1} minH={0}>
        {/* One line, not a per-format list: the help center owns the detail. */}
        <Text fontSize="sm" color="text.muted">
          Upload any file type.{' '}
          <Link href={HELP_LINKS.BATCH_IMPORT} isExternal>
            See what Bower does with each format
          </Link>
        </Text>

        {!hideConnectorHint && <ConnectorImportHint onNavigate={handleConnectorHintClick} />}

        <DropZone
          intake={intake}
          icon="upload"
          title="Drop files, or click to choose"
          next="Choose as many as you need, then Upload. They keep uploading in the tray."
        />

        {selectedFiles.length > 0 && (
          <VStack align="stretch" spacing={2} flex={1} minH={0}>
            <Text fontWeight="medium" fontSize="sm">
              Selected Files ({selectedFiles.length})
            </Text>
            <List spacing={2} flex={1} minH="64px" overflowY="auto">
              {selectedFiles.map((file, index) => (
                <ListItem
                  key={`${file.name}-${index}`}
                  p={2}
                  bg="bg.subtle"
                  // Load-bearing on a MODAL ground (bg.elevated), where the
                  // bg.subtle fill alone reads 1.013:1. Dark-only in effect.
                  borderWidth="1px"
                  borderColor="border.subtle"
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <HStack spacing={2} flex={1} overflow="hidden">
                    <MaterialIcon name={getFileIcon(file)} boxSize={5} color="text.muted" />
                    <Text fontSize="sm" noOfLines={1} flex={1}>
                      {file.name}
                    </Text>
                    <Text fontSize="xs" color="text.muted" flexShrink={0}>
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </HStack>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <MaterialIcon name="close" boxSize={4} />
                  </Button>
                </ListItem>
              ))}
            </List>
          </VStack>
        )}
      </VStack>

      <HStack justify="flex-end" spacing={3} pt={4} flexShrink={0}>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button onClick={handleImport} isDisabled={selectedFiles.length === 0}>
          Upload
        </Button>
      </HStack>
    </>
  );
}
